import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import { useApis } from "../../hooks/useApis";
import { useProducts } from "../../hooks/useProducts";
import type { OperationalError, EntityStatus } from "../../types";
import { OperationalErrorCategory } from "../../types";
import { getTenantId } from "../../utils/jwtUtils";

interface EditOperationalErrorDialogProps {
  item: OperationalError | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: OperationalError) => void;
  isCreate?: boolean;
}

const CATEGORIES = Object.values(OperationalErrorCategory);

const EditOperationalErrorDialog: React.FC<EditOperationalErrorDialogProps> = ({
  item,
  open,
  onClose,
  onSaved,
  isCreate = false,
}) => {
  const { apis } = useApis();
  const { products } = useProducts();
  const [draft, setDraft] = useState<OperationalError | null>(item);
  const [aliasInput, setAliasInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(item);
  }, [item]);

  const handleSave = async () => {
    const tenantId = getTenantId();
    if (!draft || !tenantId) return;
    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        description: draft.description ?? undefined,
        status: draft.status,
        code: draft.code ?? undefined,
        httpStatus: draft.httpStatus ?? undefined,
        category: draft.category ?? undefined,
        message: draft.message ?? undefined,
        remediation: draft.remediation ?? undefined,
        errorStrings: draft.errorStrings ?? [],
        productUuids: draft.products?.map((p) => p.uuid) ?? [],
        apiUuids: draft.apis?.map((a) => a.uuid) ?? [],
      };
      const saved = isCreate
        ? await apiService.createOperationalError({ tenantId, ...payload, status: draft.status })
        : await apiService.updateOperationalError(draft.uuid, payload);
      onSaved(saved);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isCreate ? "Add operational error" : "Edit operational error"}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          label="Code"
          margin="normal"
          value={draft?.code ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, code: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="Message"
          margin="normal"
          value={draft?.message ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, message: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="Description"
          margin="normal"
          multiline
          minRows={2}
          value={draft?.description ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, description: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="Remediation"
          margin="normal"
          multiline
          minRows={2}
          value={draft?.remediation ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, remediation: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="HTTP status"
          margin="normal"
          type="number"
          value={draft?.httpStatus ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev
                ? {
                    ...prev,
                    httpStatus: e.target.value ? Number(e.target.value) : undefined,
                  }
                : prev,
            )
          }
        />
        <TextField
          select
          fullWidth
          label="Category"
          margin="normal"
          value={draft?.category ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev
                ? { ...prev, category: e.target.value as OperationalErrorCategory }
                : prev,
            )
          }
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Status"
          margin="normal"
          value={draft?.status ?? "ACTIVE"}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, status: e.target.value as EntityStatus } : prev,
            )
          }
        >
          <MenuItem value="ACTIVE">ACTIVE</MenuItem>
          <MenuItem value="INACTIVE">INACTIVE</MenuItem>
        </TextField>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={draft?.errorStrings ?? []}
          onChange={(_, v) =>
            setDraft((prev) => (prev ? { ...prev, errorStrings: v } : prev))
          }
          inputValue={aliasInput}
          onInputChange={(_, v) => setAliasInput(v)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Error string aliases" margin="normal" placeholder="Add alias" />
          )}
        />
        <Autocomplete
          multiple
          options={products}
          getOptionLabel={(p) => p.name}
          value={draft?.products ?? []}
          onChange={(_, v) => setDraft((prev) => (prev ? { ...prev, products: v } : prev))}
          renderInput={(params) => (
            <TextField {...params} label="Products" margin="normal" />
          )}
        />
        <Autocomplete
          multiple
          options={apis}
          getOptionLabel={(a) => a.name}
          value={draft?.apis ?? []}
          onChange={(_, v) => setDraft((prev) => (prev ? { ...prev, apis: v } : prev))}
          renderInput={(params) => <TextField {...params} label="APIs" margin="normal" />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOperationalErrorDialog;
